/**
 * Project-level Punch List (2026-09-22).
 *
 * Every room's punch items on one page, because that is how the work is
 * actually done: a walkthrough produces a list, and the list is worked through
 * by vendor, not by room. Adding here picks the room; the room page's own
 * Punch tab is unchanged and reads the same rows.
 *
 * All the decisions — who an item is for, what counts as overdue, the order,
 * the counts — live in `@/lib/punch`, which is tested. This file renders them.
 */

import { esc, fmtDate } from '@/lib/dom';
import { roomHash } from '@/lib/room-links';
import { openPunchModal } from '@/lib/punch-modal';
import { updatePunch } from '@/lib/data/punch';
import { toastError, toastSuccess } from '@/lib/toast';
import { openPunchMessage } from '@/views/punch-message';
import {
  punchVendorName, isPunchOverdue, filterPunch, sortPunch, punchCounts, punchSummary,
  DEFAULT_PUNCH_FILTER, PUNCH_NO_ROOM, type PunchFilter,
} from '@/lib/punch';
import type { RoomRow, PunchItemRow, DesignerContactRow, ProjectRow } from '@/types/database';

// Module-scoped, like the Schedule view's: a filter survives the re-render
// that every edit triggers, and resets on a full navigate.
//
// It also resets when the PROJECT changes. A room filter naming a room that
// belongs to a different project would silently show an empty list on arrival,
// with the reason two screens away.
let filter: PunchFilter = { ...DEFAULT_PUNCH_FILTER };
let filterProjectId: string | null = null;

export interface RenderPunchOptions {
  project: Pick<ProjectRow, 'id' | 'name'>;
  rooms: RoomRow[];
  contacts: DesignerContactRow[];
  punch: PunchItemRow[];
  reload: () => void;
}

export function renderProjectPunchSection(container: HTMLElement, opts: RenderPunchOptions): void {
  const { rooms, contacts, punch } = opts;
  if (filterProjectId !== opts.project.id) {
    filter = { ...DEFAULT_PUNCH_FILTER };
    filterProjectId = opts.project.id;
  }
  const counts = punchCounts(punch);
  // Open items always; completed ones only when asked for, and then at the
  // bottom where they read as a record rather than as work.
  const open = sortPunch(filterPunch(punch, { ...filter, show: 'open' }), rooms);
  const done = filter.show === 'all'
    ? sortPunch(filterPunch(punch, { ...filter, show: 'all' }).filter((x) => x.complete), rooms)
    : [];
  const visible = [...open, ...done];
  // 0048 — null is a real answer now ("walk the whole house"), and it is not
  // the same as a room that was deleted out from under an item.
  const roomName = (id: string | null): string =>
    id === null ? PUNCH_NO_ROOM : rooms.find((r) => r.id === id)?.name ?? 'A deleted room';
  // Offered while any item has no room — and also while the filter IS 'none',
  // or giving the last room-less item a room would drop the option from under
  // a filter still set to it: the dropdown would read "All rooms" while the
  // list showed none of them.
  const offerNoRoom = punch.some((p) => p.room_id === null) || filter.room === 'none';

  // Only contacts actually used, plus everyone — a 60-name address book in a
  // filter for a 4-item list helps nobody.
  const usedContactIds = new Set(punch.map((p) => p.designer_contact_id).filter((x): x is string => !!x));

  container.innerHTML = `
    <section class="panel">
      <div class="section-bar">
        <div class="section-bar-left">
          <h3>Punch List</h3>
          <span class="muted small">${esc(punchSummary(counts))}</span>
          <select data-punch-filter="room" class="schedule-filter">
            <option value="all">All rooms</option>
            ${offerNoRoom ? `<option value="none" ${filter.room === 'none' ? 'selected' : ''}>${esc(PUNCH_NO_ROOM)}</option>` : ''}
            ${rooms.map((r) => `<option value="${esc(r.id)}" ${filter.room === r.id ? 'selected' : ''}>${esc(r.name)}</option>`).join('')}
          </select>
          <select data-punch-filter="vendor" class="schedule-filter">
            <option value="all">All vendors</option>
            <option value="none" ${filter.vendor === 'none' ? 'selected' : ''}>Unassigned</option>
            ${contacts.filter((c) => usedContactIds.has(c.id))
              .map((c) => `<option value="${esc(c.id)}" ${filter.vendor === c.id ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}
          </select>
        </div>
        <div class="section-bar-actions">
          <button class="btn-ghost" data-action="message-vendor">Message a vendor</button>
          <button class="btn-ghost" data-action="add-punch-item">+ Add punch item</button>
        </div>
      </div>

      ${visible.length === 0
        ? `<div class="empty-inline">${punch.length === 0
            ? 'No punch items yet. Add one here, or from a room’s Punch tab.'
            : 'No punch items match the current filters.'}</div>`
        : `<div class="punch-rows">
            <div class="punch-row punch-head">
              <span class="punch-check-head">Done</span>
              <span>Item</span><span>Room</span><span>Vendor</span><span class="punch-due">Due</span>
            </div>
            ${open.map((p) => renderRow(p, contacts, roomName)).join('')}
            ${done.length === 0 ? '' : `
              <div class="punch-done-head">Completed</div>
              ${done.map((p) => renderRow(p, contacts, roomName)).join('')}`}
          </div>`}

      ${counts.done === 0 ? '' : `
        <button class="btn-link punch-done-toggle" data-action="toggle-done">${
          filter.show === 'all'
            ? 'Hide completed'
            : `Show ${counts.done} completed`}</button>`}
    </section>
  `;

  wire(container, opts);
}

function renderRow(
  p: PunchItemRow,
  contacts: DesignerContactRow[],
  roomName: (id: string | null) => string,
): string {
  const vendor = punchVendorName(p, contacts);
  const overdue = isPunchOverdue(p);
  return `
    <div class="punch-row${p.complete ? ' done' : ''}" data-punch-id="${esc(p.id)}">
      <input type="checkbox" class="punch-check" data-action="toggle" ${p.complete ? 'checked' : ''}
             id="punch-done-${esc(p.id)}"
             aria-label="${p.complete ? 'Completed — uncheck to reopen' : 'Check to complete'}"
             title="${p.complete ? 'Completed — uncheck to reopen' : 'Check to complete'}" />
      <div class="punch-desc">
        <button class="schedule-task-btn" data-action="edit">${esc(p.description)}</button>
      </div>
      <div class="punch-room">${p.room_id === null
        // Nothing to link to: the label is the whole answer.
        ? `<span class="muted">${esc(PUNCH_NO_ROOM)}</span>`
        : `<a href="${esc(roomHash(p.room_id, 'punch'))}">${esc(roomName(p.room_id))}</a>`}</div>
      <div class="punch-vendor">${vendor ? esc(vendor) : '<span class="muted">—</span>'}</div>
      <div class="punch-due${overdue ? ' is-overdue' : ''}">${p.due_date ? esc(fmtDate(p.due_date)) : '<span class="muted">—</span>'}</div>
    </div>
  `;
}

function wire(container: HTMLElement, opts: RenderPunchOptions): void {
  const { rooms, contacts, punch, reload } = opts;

  container.querySelectorAll<HTMLSelectElement>('[data-punch-filter]').forEach((sel) => {
    sel.addEventListener('change', () => {
      const which = sel.dataset.punchFilter;
      if (which === 'room') filter = { ...filter, room: sel.value };
      else if (which === 'vendor') filter = { ...filter, vendor: sel.value };
      else if (which === 'show') filter = { ...filter, show: sel.value === 'all' ? 'all' : 'open' };
      renderProjectPunchSection(container, opts);
    });
  });

  container.querySelectorAll('[data-action="toggle-done"]').forEach((el) => {
    el.addEventListener('click', () => {
      filter = { ...filter, show: filter.show === 'all' ? 'open' : 'all' };
      renderProjectPunchSection(container, opts);
    });
  });

  container.querySelectorAll('[data-action="message-vendor"]').forEach((el) => {
    el.addEventListener('click', () => {
      openPunchMessage({
        project: opts.project, rooms, contacts, punch,
        // The vendor filter, when one is set, is almost always who the
        // designer is about to write to.
        preselectContactId: filter.vendor !== 'all' && filter.vendor !== 'none' ? filter.vendor : null,
        onSent: (name) => { toastSuccess(`Punch list sent to ${name}`); reload(); },
      });
    });
  });

  container.querySelectorAll('[data-action="add-punch-item"]').forEach((el) => {
    el.addEventListener('click', () => {
      // No "add a room first" guard since 0048: a punch item no longer needs
      // one, and walking an empty project writing down what's wrong is exactly
      // the case that asked for this.
      openPunchModal({
        rooms, contacts, projectId: opts.project.id, sortOrder: punch.length, onDone: reload,
      });
    });
  });

  container.querySelectorAll<HTMLElement>('.punch-row').forEach((row) => {
    const item = punch.find((p) => p.id === row.dataset.punchId);
    if (!item) return;

    row.querySelector('[data-action="edit"]')?.addEventListener('click', () => {
      openPunchModal({
        rooms, contacts, projectId: opts.project.id, sortOrder: item.sort_order, punch: item, onDone: reload,
      });
    });

    row.querySelector<HTMLInputElement>('[data-action="toggle"]')?.addEventListener('change', async (e) => {
      const cb = e.currentTarget as HTMLInputElement;
      try {
        await updatePunch(item.id, { complete: cb.checked });
        reload();
      } catch (err) {
        cb.checked = !cb.checked;
        toastError(err instanceof Error ? err.message : 'Could not update that item');
      }
    });
  });
}
